/*
 *  £1400 / weekends that month
 *  AM S
 *  
 *  £1500 / weekends that month
 *  PM S
 *  
 *  £15 / h
 *  OT15 S
 */

import { DateTime } from "luxon";
import { Shift } from "../../schema";
import { SHIFT_CODES_KEYS } from "../../shiftCodes";
import { HOURS_PER_WEEKEND_SHIFT, UK_AMS_PAY, UK_OT15S_PAY, UK_PMS_PAY, ShiftData, makeShiftData, UK_LAUNCH_PAY, UK_LAUNCH_PRE_17_08_2025_PAY, getMonthDateBoundaries } from "../common";

export const getWeekendsInMonth = (month: number, year: number) => {
    const date = DateTime.fromObject(
        { year, month: month, day: 1, hour: 0, minute: 0 },
        { zone: "UTC" }
    );

    const daysInMonth = date.daysInMonth || 1;
    const weekends = Array.from({ length: daysInMonth }, (_, i) =>
        DateTime.fromObject({ year, month: month, day: i + 1 }, { zone: "UTC" })
    ).filter(d => d.weekday === 6 || d.weekday === 7).length; // 6 = Sat, 7 = Sun

    console.log(`[UK Weekend Pay] Number of weekends in ${month}/${year}: ${weekends}`);
    return weekends;
}

export const getUK_AMS_WeekendMonthHourlyPay = (month: number, year: number) => {
    const weekends = getWeekendsInMonth(month, year);
    const dailyPay = UK_AMS_PAY / weekends;
    return dailyPay / HOURS_PER_WEEKEND_SHIFT;
}

export const getUK_PMS_WeekendMonthHourlyPay = (month: number, year: number) => {
    const weekends = getWeekendsInMonth(month, year);
    const dailyPay = UK_PMS_PAY / weekends;
    return dailyPay / HOURS_PER_WEEKEND_SHIFT;
}

export const calculateStandard_AM_WeekendPayForShifts = async (allShifts: Shift[], standardShiftCodeIds: string[], month: number, year: number): Promise<ShiftData[]> => {
    const weekendShifts = allShifts.filter((shift) => {
        return standardShiftCodeIds.includes(shift.shift_code);
    });

    console.log(`[UK Weekend Pay] Found ${weekendShifts.length} AM weekend shifts`);

    const hourlyPay = getUK_AMS_WeekendMonthHourlyPay(month, year);
    console.log(`[UK Weekend Pay] AM hourly pay rate: £${hourlyPay.toFixed(2)}`);

    return weekendShifts.map(shift => {
        const hours = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
        const shift_pay = hours * hourlyPay;
        return makeShiftData(shift, shift_pay);
    });
}

export const calculateStandard_PM_WeekendPayForShifts = async (allShifts: Shift[], standardShiftCodeIds: string[], month: number, year: number): Promise<ShiftData[]> => {
    const weekendShifts = allShifts.filter((shift) => {
        return standardShiftCodeIds.includes(shift.shift_code);
    });

    console.log(`[UK Weekend Pay] Found ${weekendShifts.length} PM weekend shifts`);

    const hourlyPay = getUK_PMS_WeekendMonthHourlyPay(month, year);
    console.log(`[UK Weekend Pay] PM hourly pay rate: £${hourlyPay.toFixed(2)}`);

    return weekendShifts.map(shift => {
        const hours = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
        const shift_pay = hours * hourlyPay;
        return makeShiftData(shift, shift_pay);
    });
}

export const calculateOvertime_WeekendPayForShifts = async (allShifts: Shift[], overtimeShiftCodeIds: string[]): Promise<ShiftData[]> => {
    const weekendShifts = allShifts.filter((shift) => {
        return overtimeShiftCodeIds.includes(shift.shift_code);
    });

    console.log(`[UK Weekend Pay] Found ${weekendShifts.length} overtime weekend shifts`);

    return weekendShifts.map(shift => {
        const hours = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
        const shift_pay = hours * UK_OT15S_PAY;
        return makeShiftData(shift, shift_pay);
    });
}

export const calculateLaunchPayForShifts = async (allShifts: Shift[], launchShiftCodeId: string): Promise<ShiftData[]> => {
    const weekendShifts = allShifts.filter((shift) => {
        return shift.shift_code === launchShiftCodeId;
    });
    console.log(`[UK Weekend Pay] Found ${weekendShifts.length} launch shifts`);

    return weekendShifts.map(shift => {
        const isBefore17Aug2025 = shift.start_date_time < DateTime.fromObject({ year: 2025, month: 8, day: 17, hour: 6, minute: 0, second: 0 });
        const launchPay = isBefore17Aug2025 ? UK_LAUNCH_PRE_17_08_2025_PAY : UK_LAUNCH_PAY;

        const hours = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
        const shift_pay = hours * launchPay;

        return makeShiftData(shift, shift_pay);
    });
}

export const calculateWeekendPayForShifts = async (allShifts: Shift[], amShiftCodeIds: string[], pmShiftCodeIds: string[], otShiftCodeIds: string[], launchShiftCodeIds: string[], month: number, year: number): Promise<ShiftData[]> => {
    const amPay = await calculateStandard_AM_WeekendPayForShifts(allShifts, amShiftCodeIds, month, year);
    const pmPay = await calculateStandard_PM_WeekendPayForShifts(allShifts, pmShiftCodeIds, month, year);
    const otPay = await calculateOvertime_WeekendPayForShifts(allShifts, otShiftCodeIds);
    const launchPay = await calculateLaunchPayForShifts(allShifts, launchShiftCodeIds[0]);

    otPay.push(...launchPay);

    return [
        ...amPay,
        ...pmPay,
        ...otPay
    ];
}

export const weekendPayCalculator = async (shifts: Shift[], month: number, year: number): Promise<ShiftData[]> => {
    console.log(`[UK Weekend Pay] Starting weekend pay calculation for ${month + 1}/${year}`);

    const amShiftCodes = SHIFT_CODES_KEYS.filter(sc => sc === 'AM S');
    const pmShiftCodes = SHIFT_CODES_KEYS.filter(sc => sc === 'PM S');
    const otShiftCodes = SHIFT_CODES_KEYS.filter(sc => sc === 'OT15 S');
    const launchShiftCodes = SHIFT_CODES_KEYS.filter(sc => sc === 'LAUNCH');

    const { start, end } = getMonthDateBoundaries(DateTime.fromObject({ year: year, month: month }, { zone: 'UTC' }));

    const filteredShifts = shifts.filter(shift =>
        shift.start_date_time >= start &&
        shift.start_date_time < end
    );
    console.log(`[UK Weekend Pay] Found ${filteredShifts.length} total shifts for month`);

    return await calculateWeekendPayForShifts(filteredShifts, amShiftCodes, pmShiftCodes, otShiftCodes, launchShiftCodes, month, year);
}